import * as RadixPopover from "@radix-ui/react-popover";
import React, { ReactNode, useEffect, useRef, useState } from "react";

import { CrossIcon, RightPointingAngleIcon, TickIcon } from "@/assets/icons";
import { HasErrors } from "@/components/types";
import { Spinner } from "@/components/ui/Spinner";
import { cn } from "@/lib/cn";

import { SelectOption, SelectOptionGroup, SelectOptions } from "./types";

export type ComboboxProps<TValue extends string> = HasErrors & {
  /** Id of the text input, which the field's label points at */
  id: string;
  autoFocus?: boolean;
  /**
   * False keeps the chosen options out of the control, which then shows the placeholder: for a filter
   * whose choices are listed as chips elsewhere and whose placeholder summarizes them
   */
  controlShouldRenderValue?: boolean;
  /** Id of a hint under the field, read out after the chosen value or the placeholder */
  describedBy?: string;
  /** Filters the options by what was typed; off for a search that loads its options itself */
  filterOptions?: boolean;
  isClearable?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isMulti?: boolean;
  /** Name of the field, which names the list of options "{label} options" */
  label?: string;
  noOptionsMessage: (obj: { inputValue: string }) => ReactNode;
  onChange: (value: readonly SelectOption<TValue>[]) => void;
  /** Receives every change of the typed text, an empty string once it is cleared */
  onInputChange?: (inputValue: string) => void;
  options: SelectOptions<TValue>;
  placeholder: string;
  /** "sm" is the 32px control of a toolbar, such as the page size of a table */
  size?: "default" | "sm";
  value: readonly SelectOption<TValue>[];
};

const isGroup = <TValue extends string>(
  item: SelectOption<TValue> | SelectOptionGroup<TValue>,
): item is SelectOptionGroup<TValue> => "options" in item;

/** Text the typed query is matched against: the label, or the value of an option labelled by markup */
const optionText = (option: SelectOption) =>
  typeof option.label === "string" ? option.label : option.value;

const normalize = (text: string) =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

/**
 * The text field and menu behind `Select`, `MultiSelect` and the user search: an editable combobox
 * (WAI-ARIA 1.2) whose focus stays in the input while the arrow keys move a highlight through the
 * options, announced with `aria-activedescendant`. The menu is a Radix Popover anchored to the
 * control and portalled out of side panels and scroll containers.
 */
export function Combobox<TValue extends string>(props: ComboboxProps<TValue>) {
  const {
    id,
    autoFocus,
    controlShouldRenderValue = true,
    describedBy,
    filterOptions = true,
    hasErrors,
    isClearable,
    isDisabled,
    isLoading,
    isMulti = false,
    label,
    noOptionsMessage,
    onChange,
    onInputChange,
    options,
    placeholder,
    size = "default",
    value,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const controlRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = `${id}-listbox`;
  const valueId = `${id}-value`;
  const placeholderId = `${id}-placeholder`;
  const optionId = (index: number) => `${id}-option-${index}`;

  const query = filterOptions ? normalize(inputValue.trim()) : "";
  const groups = options
    .map((item) => (isGroup(item) ? item : { label: undefined, options: [item] }))
    .map((group) => ({
      ...group,
      options: query
        ? group.options.filter((option) => normalize(optionText(option)).includes(query))
        : group.options,
    }))
    .filter((group) => group.options.length > 0);
  const visibleOptions = groups.flatMap((group) => group.options);
  const activeOption = isOpen ? visibleOptions[activeIndex] : undefined;
  const activeOptionId = activeOption ? optionId(activeIndex) : undefined;

  const isSelected = (option: SelectOption<TValue>) =>
    value.some((selected) => selected.value === option.value);
  const showsValue = value.length > 0 && (isMulti ? controlShouldRenderValue : !inputValue);
  const showsPlaceholder = !inputValue && !showsValue;

  useEffect(() => {
    if (activeOptionId) {
      // jsdom has no scrollIntoView
      document.getElementById(activeOptionId)?.scrollIntoView?.({ block: "nearest" });
    }
  }, [activeOptionId]);

  const changeInput = (text: string) => {
    setInputValue(text);
    onInputChange?.(text);
  };

  const open = (from: "first" | "last" = "first") => {
    if (isDisabled) {
      return;
    }
    const selectedIndex = isMulti ? -1 : visibleOptions.findIndex(isSelected);
    setActiveIndex(
      selectedIndex >= 0 ? selectedIndex : from === "first" ? 0 : visibleOptions.length - 1,
    );
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const choose = (option: SelectOption<TValue>) => {
    if (isMulti) {
      onChange(
        isSelected(option)
          ? value.filter((selected) => selected.value !== option.value)
          : [...value, option],
      );
      // Clearing the query lists every option again, so the highlight follows the chosen one there
      const unfiltered = options.flatMap((item) => (isGroup(item) ? item.options : [item]));
      setActiveIndex(filterOptions ? unfiltered.indexOf(option) : activeIndex);
    } else {
      onChange([option]);
      close();
    }
    if (inputValue) {
      changeInput("");
    }
  };

  const remove = (option: SelectOption<TValue>) =>
    onChange(value.filter((selected) => selected.value !== option.value));

  const clear = () => {
    onChange([]);
    changeInput("");
    inputRef.current?.focus();
  };

  const move = (step: 1 | -1) => {
    const count = visibleOptions.length;
    if (count > 0) {
      setActiveIndex((index) =>
        index < 0 || index >= count ? (step > 0 ? 0 : count - 1) : (index + step + count) % count,
      );
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        event.preventDefault();
        if (!isOpen) {
          open(event.key === "ArrowDown" ? "first" : "last");
        } else {
          move(event.key === "ArrowDown" ? 1 : -1);
        }
        break;
      case "Enter":
        if (isOpen && !event.nativeEvent.isComposing) {
          // An open menu takes Enter, so it never submits the form around the field
          event.preventDefault();
          if (activeOption) {
            choose(activeOption);
          }
        }
        break;
      case "Escape":
        if (isOpen || inputValue) {
          // Escape closes the menu, not the panel or dialog that holds the field
          event.preventDefault();
          event.stopPropagation();
          close();
          changeInput("");
        }
        break;
      case "Backspace":
        if (!inputValue && value.length > 0) {
          if (isMulti && controlShouldRenderValue) {
            remove(value[value.length - 1]);
          } else if (!isMulti && isClearable) {
            onChange([]);
          }
        }
        break;
    }
  };

  const handleControlMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isDisabled || event.button !== 0) {
      return;
    }
    const onInput = event.target === inputRef.current;
    if (!onInput) {
      // Keeps the input focused, as a press anywhere on the control is a press on the field
      event.preventDefault();
      inputRef.current?.focus();
    }
    if (!isOpen) {
      open();
    } else if (!onInput) {
      close();
    }
  };

  /** A button inside the control acts on click and leaves focus in the input */
  const keepFocus = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  let optionIndex = 0;
  const renderOption = (option: SelectOption<TValue>) => {
    const index = optionIndex++;
    const selected = isSelected(option);
    return (
      <div
        key={option.value}
        id={optionId(index)}
        role="option"
        aria-selected={selected}
        tabIndex={-1}
        className={cn(
          "flex cursor-pointer items-start gap-2 px-3 py-1.5 font-semibold text-foreground",
          size === "sm" && "text-xs",
          index === activeIndex && "bg-muted",
        )}
        onMouseMove={() => index !== activeIndex && setActiveIndex(index)}
        onClick={() => choose(option)}
      >
        <span className="min-w-0 flex-1 wrap-anywhere">{option.label}</span>
        {selected && <TickIcon aria-hidden className="mt-1 h-3 w-auto shrink-0 text-primary-fg" />}
      </div>
    );
  };

  return (
    <RadixPopover.Root open={isOpen} onOpenChange={(nextOpen) => !nextOpen && close()}>
      <RadixPopover.Anchor asChild>
        {/* The input inside takes the keyboard; a press anywhere else on the control focuses it */}
        {}
        <div
          ref={controlRef}
          className={cn(
            "flex w-full cursor-text items-center gap-2 rounded-sm border border-border bg-background px-3 text-base font-semibold text-foreground outline-transparent transition-colors focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring md:text-sm",
            size === "sm" ? "min-h-8 text-xs md:text-xs" : "min-h-10",
            hasErrors && "border-destructive",
            isDisabled && "cursor-not-allowed opacity-50",
          )}
          onMouseDown={handleControlMouseDown}
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1 py-1">
            {isMulti && showsValue && (
              <div id={valueId} className="contents">
                {value.map((option) => (
                  <span
                    key={option.value}
                    className="inline-flex min-w-0 items-stretch rounded-sm bg-muted text-xs"
                  >
                    <span className="min-w-0 px-1.5 py-0.5 wrap-anywhere">{option.label}</span>
                    {!isDisabled && (
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label={`Remove ${optionText(option)}`}
                        className="flex items-center rounded-r px-1 text-muted-foreground hover:bg-destructive-subtle hover:text-destructive-fg"
                        onMouseDown={keepFocus}
                        onClick={() => remove(option)}
                      >
                        <CrossIcon aria-hidden className="h-2 w-auto" />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}
            <div className="grid min-w-8 flex-1 items-center">
              {showsPlaceholder && (
                <span
                  id={placeholderId}
                  className="col-start-1 row-start-1 truncate text-muted-foreground"
                >
                  {placeholder}
                </span>
              )}
              {!isMulti && showsValue && (
                <span
                  id={valueId}
                  className={cn(
                    "col-start-1 row-start-1 truncate",
                    hasErrors && "text-destructive-fg",
                  )}
                >
                  {value[0].label}
                </span>
              )}
              <input
                ref={inputRef}
                id={id}
                type="text"
                role="combobox"
                aria-expanded={isOpen}
                aria-controls={isOpen ? listboxId : undefined}
                aria-autocomplete="list"
                aria-activedescendant={activeOptionId}
                aria-describedby={
                  [showsPlaceholder ? placeholderId : showsValue ? valueId : undefined, describedBy]
                    .filter(Boolean)
                    .join(" ") || undefined
                }
                aria-invalid={hasErrors || undefined}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                autoFocus={autoFocus}
                disabled={isDisabled}
                value={inputValue}
                className="col-start-1 row-start-1 w-full min-w-0 border-none bg-transparent p-0 font-semibold text-inherit outline-hidden disabled:cursor-not-allowed"
                onChange={(event) => {
                  changeInput(event.currentTarget.value);
                  if (!isOpen) {
                    open();
                  }
                  setActiveIndex(0);
                }}
                onKeyDown={handleKeyDown}
                onBlur={() => {
                  close();
                  if (inputValue) {
                    changeInput("");
                  }
                }}
              />
            </div>
          </div>
          {isLoading && <Spinner aria-hidden size={16} />}
          {isClearable && value.length > 0 && !isDisabled && (
            <button
              type="button"
              tabIndex={-1}
              aria-label="Clear selection"
              className="flex text-muted-foreground hover:text-primary-fg"
              onMouseDown={keepFocus}
              onClick={clear}
            >
              <CrossIcon aria-hidden className="h-2.5 w-auto" />
            </button>
          )}
          <RightPointingAngleIcon
            aria-hidden
            className={cn(
              "shrink-0 cursor-pointer text-muted-foreground transition-transform duration-100 ease-in-out",
              isOpen ? "-rotate-90" : "rotate-90",
            )}
          />
        </div>
      </RadixPopover.Anchor>
      <RadixPopover.Portal>
        <RadixPopover.Content
          asChild
          side="bottom"
          align="start"
          sideOffset={4}
          collisionPadding={8}
          // Focus stays in the input, which drives the menu
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            if (controlRef.current?.contains(event.target as Node)) {
              event.preventDefault();
            }
          }}
        >
          {/* A press in the menu must not blur the input, which would close it */}
          {}
          <div
            className="z-1010 max-h-[min(300px,var(--radix-popover-content-available-height))] w-(--radix-popover-trigger-width) overflow-y-auto rounded-sm border border-border bg-background py-1 text-sm shadow-soft"
            onMouseDown={(event) => event.preventDefault()}
          >
            <div
              id={listboxId}
              role="listbox"
              aria-label={label && `${label} options`}
              aria-multiselectable={isMulti || undefined}
            >
              {groups.map((group, groupIndex) =>
                group.label === undefined ? (
                  group.options.map(renderOption)
                ) : (
                  <div key={group.label} role="group" aria-labelledby={`${id}-group-${groupIndex}`}>
                    <div
                      id={`${id}-group-${groupIndex}`}
                      className="px-3 pb-1 pt-2 text-xs font-medium text-muted-foreground"
                    >
                      {group.label}
                    </div>
                    {group.options.map(renderOption)}
                  </div>
                ),
              )}
            </div>
            {visibleOptions.length === 0 && (
              <div role="status" className="px-3 py-1.5 text-center text-muted-foreground">
                {isLoading ? <Spinner size={24} /> : noOptionsMessage({ inputValue })}
              </div>
            )}
          </div>
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}
